import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import {
  Smartphone, Download, ArrowRight, CheckCircle2,
  Share, Plus, ChevronRight, Sparkles
} from 'lucide-react';

const WelcomeHome = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const fr = language === 'fr';

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installTriggered, setInstallTriggered] = useState(false);

  useEffect(() => {
    const installed = window.matchMedia('(display-mode: standalone)').matches;
    setIsInstalled(installed);
    const ua = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua));
    setIsAndroid(/Android/.test(ua));

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstallTriggered(true);
      }
      setDeferredPrompt(null);
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  const userName = user?.name?.split(' ')[0] || '';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg space-y-6 py-8"
      >
        {/* Success check */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="flex justify-center"
        >
          <div className="w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
        </motion.div>

        {/* Welcome message */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center space-y-3"
        >
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-foreground">
            {t('pages.welcome_home', { name: userName })}
          </h1>
          <p className="text-muted-foreground text-base max-w-sm mx-auto leading-relaxed">
            {fr
              ? "Ton compte Extra Miles est pret. Tu viens de débloquer un outil de haute performance pour ta transformation."
              : "Your Extra Miles account is ready. You just unlocked a high-performance tool for your transformation."}
          </p>
        </motion.div>

        {/* PWA Install Card */}
        {!isInstalled && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-card border border-border rounded-2xl p-5 space-y-4"
            data-testid="welcome-pwa-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground">
                  {t('pages.install_app')}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('pages.install_app_desc')}
                </p>
              </div>
            </div>

            {/* Android with prompt available */}
            {(deferredPrompt || installTriggered) && !isIOS && (
              <div className="space-y-3">
                {!installTriggered ? (
                  <Button
                    onClick={handleInstall}
                    className="w-full gap-2 h-12 font-heading uppercase tracking-wider"
                    data-testid="welcome-install-btn"
                  >
                    <Download className="w-5 h-5" />
                    {t('pages.install_yumix')}
                  </Button>
                ) : (
                  <div className="flex items-center justify-center gap-2 py-3 text-primary text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    {fr ? "Installation en cours..." : "Installing..."}
                  </div>
                )}
              </div>
            )}

            {/* iOS Instructions */}
            {isIOS && (
              <div className="space-y-3" data-testid="welcome-ios-instructions">
                <div className="bg-secondary/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-sm font-bold text-blue-400">1</span>
                    </div>
                    <div>
                      <p className="text-sm text-foreground">
                        {fr ? "Appuie sur le bouton" : "Tap the"} <Share className="w-4 h-4 inline text-blue-400" /> <span className="font-semibold text-foreground">{fr ? "Partager" : "Share"}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">{fr ? "en bas de Safari" : "at the bottom of Safari"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-sm font-bold text-blue-400">2</span>
                    </div>
                    <div>
                      <p className="text-sm text-foreground">
                        {fr ? "Choisis" : "Choose"} <Plus className="w-4 h-4 inline text-foreground" /> <span className="font-semibold text-foreground">{fr ? '"Sur l\'ecran d\'accueil"' : '"Add to Home Screen"'}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-sm font-bold text-primary">3</span>
                    </div>
                    <div>
                      <p className="text-sm text-foreground">
                        {fr ? "Appuie" : "Tap"} <span className="font-semibold text-primary">{fr ? '"Ajouter"' : '"Add"'}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Android without prompt (manual) */}
            {isAndroid && !deferredPrompt && !installTriggered && (
              <div className="bg-secondary/50 rounded-xl p-4 space-y-2" data-testid="welcome-android-instructions">
                <p className="text-sm text-foreground">
                  {fr ? "Appuie sur" : "Tap"} <span className="font-bold">&#8942;</span> {fr ? "en haut a droite, puis" : "in the top right, then"} <span className="font-semibold text-foreground">{fr ? '"Installer l\'application"' : '"Install app"'}</span>
                </p>
              </div>
            )}

            {/* Desktop */}
            {!isIOS && !isAndroid && !deferredPrompt && (
              <p className="text-xs text-muted-foreground text-center">
                {fr
                  ? "Tu peux aussi installer cette app via le menu de ton navigateur."
                  : "You can also install this app via your browser menu."}
              </p>
            )}
          </motion.div>
        )}

        {/* Already installed badge */}
        {isInstalled && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center justify-center gap-2 py-3 bg-primary/10 rounded-xl text-primary text-sm"
          >
            <CheckCircle2 className="w-4 h-4" />
            {fr ? "Application déjà installee !" : "App already installed!"}
          </motion.div>
        )}

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Button
            onClick={goToDashboard}
            className="w-full gap-2 h-14 text-lg font-heading uppercase tracking-wider"
            data-testid="welcome-dashboard-btn"
          >
            <Sparkles className="w-5 h-5" />
            {fr ? 'Ouvrir mon Tableau de Bord' : 'Open my Dashboard'}
            <ArrowRight className="w-5 h-5" />
          </Button>
        </motion.div>

        {/* Subtle branding */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center text-xs text-muted-foreground/50"
        >
          YUMIX — Extra Miles
        </motion.p>
      </motion.div>
    </div>
  );
};

export default WelcomeHome;
